<template>
  <div class="date-filter">
    <!-- Shortcuts -->
    <div class="filter-bar__presets">
      <button
        v-for="s in shortcuts"
        :key="s.value"
        :class="{ active: active === s.value }"
        @click="selectShortcut(s.value)"
      >{{ s.label }}</button>
    </div>

    <!-- Rango personalizado (calendario) -->
    <div class="date-filter__range">
      <button
        class="date-filter__toggle"
        :class="{ active: active === 'custom' }"
        @click="togglePicker"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
          <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
        <span>{{ rangeLabel }}</span>
      </button>

      <template v-if="showPicker">
        <div class="date-filter__backdrop" @click="showPicker = false"></div>
        <div class="date-filter__pop">
          <v-date-picker
            v-model="range"
            is-range
            is-expanded
            color="red"
            :timezone="'America/Lima'"
          />
          <div class="date-filter__pop-actions">
            <button class="date-filter__cancel" @click="showPicker = false">Cancelar</button>
            <button class="btn btn--small" :disabled="!canApply" @click="applyCustom">Aplicar</button>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
// Filtro de fecha estándar del panel: shortcuts (un click = aplica) + rango
// personalizado con calendario (v-date-picker is-range, anclado a America/Lima).
// Emite @change con { preset, from, to } donde from/to son 'YYYY-MM-DD' (o null
// para 'Todo'); cada módulo corre su propia consulta. Reutilizable en todos los
// módulos con filtro por fecha.
export default {
  name: 'DateFilter',
  props: {
    // Preset activo inicial (uno de los value de shortcuts, o 'custom')
    modelValue: { type: String, default: 'month' }
  },
  emits: ['update:modelValue', 'change'],
  data () {
    return {
      active: this.modelValue,
      showPicker: false,
      range: null, // { start: Date, end: Date } del v-date-picker
      shortcuts: [
        { value: 'today', label: 'Hoy' },
        { value: 'week', label: 'Esta semana' },
        { value: 'month', label: 'Este mes' },
        { value: 'prev', label: 'Mes pasado' },
        { value: 'year', label: 'Este año' },
        { value: 'all', label: 'Todo' }
      ]
    }
  },
  computed: {
    canApply () {
      return !!(this.range && this.range.start && this.range.end)
    },
    rangeLabel () {
      if (this.active === 'custom' && this.canApply) {
        return this.fmtShort(this.range.start) + ' – ' + this.fmtShort(this.range.end)
      }
      return 'Rango'
    }
  },
  created () {
    // Emitir el rango inicial según el preset por defecto (sin doble carga en el padre).
    this.emitRange(this.active)
  },
  methods: {
    // 'YYYY-MM-DD' con las partes locales (navegador en Lima) — sin el corrimiento
    // de zona que introduce toISOString().
    fmt (d) {
      return d.getFullYear() + '-' +
        String(d.getMonth() + 1).padStart(2, '0') + '-' +
        String(d.getDate()).padStart(2, '0')
    },
    fmtShort (d) {
      const x = d instanceof Date ? d : new Date(d)
      return String(x.getDate()).padStart(2, '0') + '/' + String(x.getMonth() + 1).padStart(2, '0')
    },
    // Calcula { preset, from, to } para un shortcut, anclado al día local (Lima).
    computeRange (value) {
      const now = new Date()
      const y = now.getFullYear()
      const m = now.getMonth()
      const d = now.getDate()
      let from
      let to
      if (value === 'today') {
        from = new Date(y, m, d); to = new Date(y, m, d)
      } else if (value === 'week') {
        const dow = (now.getDay() + 6) % 7 // 0 = lunes
        from = new Date(y, m, d - dow); to = new Date(y, m, d - dow + 6)
      } else if (value === 'month') {
        from = new Date(y, m, 1); to = new Date(y, m + 1, 0)
      } else if (value === 'prev') {
        from = new Date(y, m - 1, 1); to = new Date(y, m, 0)
      } else if (value === 'year') {
        from = new Date(y, 0, 1); to = new Date(y, 11, 31)
      } else {
        return { preset: 'all', from: null, to: null }
      }
      return { preset: value, from: this.fmt(from), to: this.fmt(to) }
    },
    emitRange (value) {
      this.$emit('change', this.computeRange(value))
    },
    selectShortcut (value) {
      this.active = value
      this.showPicker = false
      this.$emit('update:modelValue', value)
      this.emitRange(value)
    },
    togglePicker () {
      this.showPicker = !this.showPicker
    },
    applyCustom () {
      if (!this.canApply) return
      this.active = 'custom'
      this.showPicker = false
      this.$emit('update:modelValue', 'custom')
      this.$emit('change', {
        preset: null,
        from: this.fmt(this.range.start),
        to: this.fmt(this.range.end)
      })
    }
  }
}
</script>

<style scoped>
.date-filter {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 12px 24px;
}

.date-filter__range {
  position: relative;
}

/* Toggle de rango — pill igual que los shortcuts (.filter-bar__presets) */
.date-filter__toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 14px;
  /* #C9C9CE, no #E4E4E7: el borde tiene que verse sobre el gris de la página */
  border: 1.5px solid #C9C9CE;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  color: #71717A;
  background: transparent;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}
.date-filter__toggle:hover { border-color: #18181B; color: #18181B; background: rgba(0, 0, 0, 0.03); }
.date-filter__toggle.active { background: #18181B; border-color: #18181B; color: #fff; }
.date-filter__toggle svg { width: 13px; height: 13px; }

/* Backdrop invisible para cerrar al hacer click fuera */
.date-filter__backdrop {
  position: fixed;
  inset: 0;
  z-index: 40;
}

/* Popover del calendario */
.date-filter__pop {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 50;
  width: 300px;
  background: #FFFFFF;
  border: 1px solid #E4E4E7;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}
.date-filter__pop-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
}
.date-filter__cancel {
  background: transparent;
  border: none;
  color: #71717A;
  font-size: 12px;
  cursor: pointer;
  padding: 0 8px;
}
.date-filter__cancel:hover { color: #18181B; }
</style>
